/**
 * Private Route Guard
 *
 * Protects routes that require authentication.
 * Redirects to login page with returnUrl when not authenticated.
 */

import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Navigate, Outlet, useLocation, useNavigate,
} from "react-router";
import { removeFilterParams } from "@/shared/utils/persistedFilters";
import type { RootState } from "@/store";

const PrivateRoute: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, hasResetFilters } = useSelector((state: RootState) => state.session);

  // After a voluntary logout of an élu who does not remember their filters, the
  // next login starts from the default filters; otherwise the user gets the page
  // back as it was.
  const returnSearch = hasResetFilters
    ? removeFilterParams(location.pathname, location.search)
    : location.search;
  const returnUrl = location.pathname + returnSearch;

  useEffect(() => {
    if (isAuthenticated) return;
    navigate("/login", { state: { returnUrl } });
  }, [isAuthenticated, navigate, returnUrl]);

  return isAuthenticated ? <Outlet /> : <Navigate to="/login" />;
};

export default PrivateRoute;
